import type { FC } from "react"
import { useState } from "react"
import { observer } from "mobx-react-lite"

import { tickToPriceString } from "@/domains/manual-cl-core"

import type { ManualClPositionStore } from "../../positions/manual-cl-position-store"
import type { ThickV1TickDistributionStore } from "../../tick-distribution"
import { RangeBrushView } from "./range-brush-view"

export interface ManagePositionRangeBrushCompositeProps {
  positionStore: ManualClPositionStore
  tickDistributionStore: ThickV1TickDistributionStore
  className?: string
  height?: number
}

/**
 * Read-only variant of the range brush used on Increase / Decrease / Collect /
 * Burn manage views. The tick bounds come from the NFP position itself (they
 * are immutable until burn), so no `setMinTick`/`setMaxTick` wiring is needed.
 * The `invert` toggle remains interactive for display convenience.
 */
export const ManagePositionRangeBrushComposite: FC<ManagePositionRangeBrushCompositeProps> =
  observer(({ positionStore, tickDistributionStore, className, height }) => {
    const [invert, setInvert] = useState(false)

    const pool = positionStore.pool
    const tickSpacing = tickDistributionStore.tickSpacing
    const currentTick = pool?.currentTick

    if (!pool) {
      return (
        <RangeBrushView
          bins={[]}
          currentTick={undefined}
          tickSpacing={1}
          minTick={0}
          maxTick={0}
          baseSymbol="—"
          quoteSymbol="—"
          isLoading
          readOnly
          className={className}
          height={height}
        />
      )
    }

    const token0 = pool.token0
    const token1 = pool.token1

    const formatTickPrice = (tick: number): string =>
      tickToPriceString({ tick, token0, token1, invert, decimalPlaces: 6 })

    return (
      <RangeBrushView
        bins={tickDistributionStore.binsInWindow}
        currentTick={currentTick}
        tickSpacing={tickSpacing}
        minTick={positionStore.tickLower}
        maxTick={positionStore.tickUpper}
        baseSymbol={invert ? token1.symbol : token0.symbol}
        quoteSymbol={invert ? token0.symbol : token1.symbol}
        formatTickPrice={formatTickPrice}
        invert={invert}
        onInvertToggle={() => setInvert((prev) => !prev)}
        readOnly
        isLoading={tickDistributionStore.isLoading && tickDistributionStore.bins.length === 0}
        className={className}
        height={height}
      />
    )
  })
